<template>
  <div>
    <woot-modal-header :header-title="filterModalHeaderTitle">
      <p class="text-slate-600 dark:text-slate-200">
        {{ filterModalSubTitle }}
      </p>
    </woot-modal-header>
    <div class="p-8">
      <div v-if="isBoardFilterView">
        <label class="input-label" :class="{ error: !activeBoardFilterNewName }">
          {{ $t('BOARD_FILTER.FILTER_LABEL') }}
          <input
            v-model="activeBoardFilterNewName"
            type="text"
            class="folder-input border-slate-75 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          />
          <span v-if="!activeBoardFilterNewName" class="message">
            {{ $t('BOARD_FILTER.EMPTY_VALUE_ERROR') }}
          </span>
        </label>
        <label class="input-label">
          {{ $t('BOARD_FILTER.FILTER_QUERY_LABEL') }}
        </label>
      </div>
      <div
        class="p-4 rounded-lg bg-slate-25 dark:bg-slate-900 border border-solid border-slate-50 dark:border-slate-700/50 mb-4"
      >
        <filter-input-box
          v-for="(filter, i) in appliedFilters"
          :key="i"
          v-model="appliedFilters[i]"
          :filter-groups="filterGroups"
          :grouped-filters="true"
          :input-type="
            getInputType(
              appliedFilters[i].attribute_key,
              appliedFilters[i].filter_operator
            )
          "
          :operators="getOperators(appliedFilters[i].attribute_key)"
          operator-translation-key="BOARD_FILTER.OPERATOR_LABELS"
          :dropdown-values="getDropdownValues(appliedFilters[i].attribute_key)"
          :show-query-operator="i !== appliedFilters.length - 1"
          :show-user-input="showUserInput(appliedFilters[i].filter_operator)"
          :v="$v.appliedFilters.$each[i]"
          @resetFilter="resetFilter(i, appliedFilters[i])"
          @removeFilter="removeFilter(i)"
        />
        <div class="mt-4 flex gap-2">
          <woot-button
            icon="add"
            color-scheme="success"
            variant="smooth"
            size="small"
            @click="appendNewFilter"
          >
            {{ $t('BOARD_FILTER.ADD_NEW_FILTER') }}
          </woot-button>
          <woot-button
            v-if="hasSubmittedAppliedFilters && !isBoardFilterView"
            icon="subtract"
            color-scheme="alert"
            variant="smooth"
            size="small"
            @click="clearFilters"
          >
            {{ $t('BOARD_FILTER.CLEAR_ALL_FILTERS') }}
          </woot-button>
        </div>
      </div>
      <div class="w-full">
        <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
          <woot-button class="button clear" @click.prevent="onClose">
            {{ $t('BOARD_FILTER.CANCEL_BUTTON_LABEL') }}
          </woot-button>
          <woot-button
            v-if="isBoardFilterView"
            :disabled="!activeBoardFilterNewName"
            @click="updateBoardFilter"
          >
            {{ $t('BOARD_FILTER.UPDATE_BUTTON_LABEL') }}
          </woot-button>
          <woot-button v-else @click="submitFilterQuery">
            {{ $t('BOARD_FILTER.SUBMIT_BUTTON_LABEL') }}
          </woot-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import { required } from 'vuelidate/lib/validators';
import FilterInputBox from '../../../../components/widgets/FilterInput/Index.vue';
import { mapGetters } from 'vuex';
import boardFilterTypes, { boardFilterItems } from './boardFilterItems';
import filterMixin from 'shared/mixins/filterMixin';
import * as OPERATORS from 'dashboard/components/widgets/FilterInput/FilterOperatorTypes.js';

export default {
  components: {
    FilterInputBox,
  },
  mixins: [alertMixin, filterMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    initialFilterTypes: {
      type: Array,
      default: () => [],
    },
    initialAppliedFilters: {
      type: Array,
      default: () => [],
    },
    isBoardFilterView: {
      type: Boolean,
      default: false,
    },
    activeBoardFilterName: {
      type: String,
      default: '',
    },
    currentBoardId: {
      type: [Number, String],
      default: null,
    },
  },
  validations: {
    appliedFilters: {
      required,
      $each: {
        values: {
          required,
          ensureBetween0to999(value, prop) {
            if (prop.filter_operator === 'days_before') {
              return parseInt(value, 10) > 0 && parseInt(value, 10) < 999;
            }
            return true;
          },
        },
      },
    },
  },
  data() {
    return {
      show: true,
      appliedFilters: JSON.parse(JSON.stringify(this.initialAppliedFilters || [])),
      activeBoardFilterNewName: this.activeBoardFilterName,
      filterTypes: [
        ...this.initialFilterTypes.filter(
          filter => filter.attributeKey !== 'board_id'
        ),
        {
          attributeKey: 'assignee_id',
          attributeI18nKey: 'BOARD_FILTER.ATTRIBUTES.ASSIGNEE',
          inputType: 'search_select',
          filterOperators: OPERATORS.OPERATOR_TYPES_1,
          attributeModel: 'standard',
        },
      ],
      filterGroups: [],
      allCustomAttributes: [],
      rawFilterAttributeGroups: boardFilterItems,
      attributeModel: 'contact_attribute',
      filtersFori18n: 'BOARD_FILTER',
      agents: [],
      isLoadingAgents: false,
    };
  },
  computed: {
    ...mapGetters({
      pipelineBoards: 'pipelines/getAllBoards',
      currentAccountId: 'getCurrentAccountId',
      labels: 'labels/getLabels',
      states: 'pipelines/getAllStatus',
      inboxes: 'inboxes/getInboxes',
    }),
    hasAppliedFilters() {
      return this.appliedFilters.length;
    },
    hasActiveAppliedFilters() {
      return this.appliedFilters.some(filter => this.hasValidValue(filter));
    },
    hasSubmittedAppliedFilters() {
      return (this.initialAppliedFilters || []).some(filter =>
        this.hasValidValue(filter)
      );
    },
    filterModalHeaderTitle() {
      return !this.isBoardFilterView
        ? this.$t('BOARD_FILTER.TITLE')
        : this.$t('BOARD_FILTER.EDIT_CUSTOM_FILTER');
    },
    filterModalSubTitle() {
      return !this.isBoardFilterView
        ? this.$t('BOARD_FILTER.SUBTITLE')
        : this.$t('BOARD_FILTER.CUSTOM_VIEWS_SUBTITLE');
    },
    filterAttributeGroups() {
      return this.rawFilterAttributeGroups.map(group => ({
        ...group,
        attributes: group.attributes.filter(attr => attr.key !== 'board_id'),
      }));
    },
  },
  async mounted() {
    this.$store.dispatch('pipelines/getBoards');
    this.$store.dispatch('pipelines/getState');
    this.$store.dispatch('inboxes/get');
    this.fetchAgents();

    this.setFilterAttributes();
    
    // Remove Custom Attributes from filter groups
    this.filterGroups = this.filterGroups.filter(
      group => group.name !== this.$t('BOARD_FILTER.GROUPS.CUSTOM_ATTRIBUTES')
    );
    
    if (!this.isBoardFilterView && this.initialAppliedFilters.length) {
      this.appliedFilters = JSON.parse(
        JSON.stringify(this.initialAppliedFilters)
      );
    } else if (!this.isBoardFilterView) {
      this.appliedFilters.push({
        attribute_key: 'contact_name',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        attribute_model: 'standard',
      });
    }
  },
  methods: {
    isEmptyValue(value) {
      if (value === null || value === undefined) return true;
      if (typeof value === 'string' && value.trim() === '') return true;
      if (Array.isArray(value) && value.length === 0) return true;
      if (Array.isArray(value)) {
        return value.every(item => {
          if (item === null || item === undefined) return true;
          if (typeof item === 'string' && item.trim() === '') return true;
          if (typeof item === 'object' && Object.keys(item).length === 0) return true;
          if (
            typeof item === 'object' &&
            item.id !== undefined &&
            (item.id === null || item.id === undefined || item.id === '')
          ) {
            return true;
          }
          return false;
        });
      }
      if (typeof value === 'object' && !Array.isArray(value)) {
        if (Object.keys(value).length === 0) return true;
        if (
          value.id !== undefined &&
          (value.id === null || value.id === undefined || value.id === '')
        ) {
          return true;
        }
      }
      return false;
    },
    hasValidValue(item) {
      const operatorsWithoutValues = ['is_present', 'is_not_present'];
      if (operatorsWithoutValues.includes(item.filter_operator)) {
        return true;
      }
      return !this.isEmptyValue(item.values);
    },
    async fetchAgents() {
      try {
        const response = await axios.get(
          `/api/v1/accounts/${this.currentAccountId}/agents`
        );
        this.agents = response.data.map(agent => ({
          id: agent.id,
          name: agent.name,
        }));
      } catch (error) {
        this.showAlert(this.$t('BOARD_FILTER.AGENT_FETCH_ERROR'));
        this.agents = [];
      }
    },
    getOperatorTypes(key) {
      switch (key) {
        case 'list':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'text':
          return OPERATORS.OPERATOR_TYPES_3;
        case 'number':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'link':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'date':
          return OPERATORS.OPERATOR_TYPES_4;
        case 'checkbox':
          return OPERATORS.OPERATOR_TYPES_1;
        default:
          return OPERATORS.OPERATOR_TYPES_1;
      }
    },
    customAttributeInputType(key) {
      switch (key) {
        case 'date':
          return 'date';
        case 'text':
          return 'plain_text';
        case 'list':
          return 'search_select';
        case 'checkbox':
          return 'search_select';
        default:
          return 'plain_text';
      }
    },
    getAttributeModel(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type.attributeModel;
    },
    getInputType(key, operator) {
      if (key === 'created_at' || key === 'last_activity_at') {
        if (operator === 'days_before') return 'plain_text';
        if (key === 'created_at') return 'datetime';
        return 'date';
      }
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.inputType;
    },
    getOperators(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.filterOperators;
    },
    getDropdownValues(type) {
      if (type === 'assignee_id') {
        return this.agents;
      }
      if (type === 'board_id') {
        return this.pipelineBoards.map(board => ({
          id: board.id,
          name: board.name,
          statuses_attributes: board.statuses_attributes,
        }));
      }
      if (type === 'status_id') {
        const currentBoard = this.pipelineBoards.find(
          board => String(board.id) === String(this.currentBoardId)
        );

        const statuses = currentBoard
          ? currentBoard.statuses_attributes || []
          : this.pipelineBoards.flatMap(board => board.statuses_attributes || []);

        return statuses.map(status => ({
          id: status.id,
          name: status.name,
        }));
      }
      if (type === 'state') {
        return this.states.map(state => ({
          id: state.id,
          name: state.name,
        }));
      }
      if (type === 'source_of_lead') {
        return this.inboxes.map(inbox => ({
          id: inbox.id,
          name: inbox.name,
        }));
      }
      if (type === 'last_activity_by') {
        return [
          { id: 'user', name: 'Last Activity User' },
          { id: 'agent', name: 'Last Activity Agent' },
        ];
      }
      if (type === 'labels_id') {
        return this.labels.map(lab => ({
          id: lab.id,
          name: lab.title,
        }));
      }
      
      const allCustomAttributes = this.$store.getters[
        'attributes/getAttributesByModel'
      ](this.attributeModel);
      const isCustomAttributeCheckbox = allCustomAttributes.find(attr => {
        return (
          attr.attribute_key === type &&
          attr.attribute_display_type === 'checkbox'
        );
      });
      if (isCustomAttributeCheckbox) {
        return [
          {
            id: true,
            name: this.$t('FILTER.ATTRIBUTE_LABELS.TRUE'),
          },
          {
            id: false,
            name: this.$t('FILTER.ATTRIBUTE_LABELS.FALSE'),
          },
        ];
      }

      const isCustomAttributeList = allCustomAttributes.find(attr => {
        return (
          attr.attribute_key === type && attr.attribute_display_type === 'list'
        );
      });

      if (isCustomAttributeList) {
        return allCustomAttributes
          .find(attr => attr.attribute_key === type)
          .attribute_values.map(item => {
            return {
              id: item,
              name: item,
            };
          });
      }
      return undefined;
    },
    appendNewFilter() {
      if (this.isBoardFilterView) {
        this.setQueryOperatorOnLastQuery();
      } else {
        this.appliedFilters.push({
          attribute_key: 'contact_name',
          filter_operator: 'equal_to',
          values: '',
          query_operator: 'and',
        });
      }
    },
    setQueryOperatorOnLastQuery() {
      const lastItemIndex = this.appliedFilters.length - 1;
      this.appliedFilters[lastItemIndex] = {
        ...this.appliedFilters[lastItemIndex],
        query_operator: 'and',
      };
      this.$nextTick(() => {
        this.appliedFilters.push({
          attribute_key: 'contact_name',
          filter_operator: 'equal_to',
          values: '',
          query_operator: 'and',
        });
      });
    },
    removeFilter(index) {
      if (this.appliedFilters.length <= 1) {
        this.showAlert(this.$t('BOARD_FILTER.FILTER_DELETE_ERROR'));
      } else {
        this.appliedFilters.splice(index, 1);
      }
    },
    submitFilterQuery() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      const validFilters = this.appliedFilters.filter(this.hasValidValue);

      if (validFilters.length === 0 && !this.isBoardFilterView) {
        this.$emit('clearFilters');
        this.onClose();
        return;
      }

      const payload = {
        filters: JSON.parse(JSON.stringify(validFilters)),
      };

      this.$store.dispatch(
        'pipelines/setBoardFilters',
        {
          boardId: this.currentBoardId,
          filters: JSON.parse(JSON.stringify(validFilters)),
        }
      );

      this.$emit('applyFilter', payload);
    },
    updateBoardFilter() {
      const validFilters = this.appliedFilters.filter(this.hasValidValue);

      this.$emit(
        'updateBoardFilter',
        validFilters,
        this.activeBoardFilterNewName
      );
    },
    resetFilter(index, currentFilter) {
      this.appliedFilters[index].filter_operator = this.filterTypes.find(
        filter => filter.attributeKey === currentFilter.attribute_key
      ).filterOperators[0].value;
      this.appliedFilters[index].values = '';
    },
    showUserInput(operatorType) {
      if (operatorType === 'is_present' || operatorType === 'is_not_present')
        return false;
      return true;
    },
    clearFilters() {
      this.$emit('clearFilters');
      this.onClose();
    },
  },
};
</script>

<style lang="scss" scoped>
.folder-input {
  @apply w-[50%];
}
</style>
